import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from "react";
import type { LoginInput, User } from "@plataforma/shared";
import { api, ApiError, SESSION_EXPIRED_EVENT } from "../lib/api";

interface AuthValue {
  user: User | null;
  loading: boolean;
  login: (input: LoginInput) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);
const ME = ["auth", "me"];

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const me = useQuery({
    queryKey: ME,
    queryFn: async () => {
      try {
        return (await api.get<{ user: User }>("/auth/me")).user;
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    retry: false,
    staleTime: Infinity,
  });

  // Sessão expirada em qualquer tela volta para o login, sem deixar dados antigos em cache.
  useEffect(() => {
    const onExpired = () => {
      queryClient.clear();
      queryClient.setQueryData(ME, null);
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, [queryClient]);

  const login = useCallback(
    async (input: LoginInput) => {
      const { user } = await api.post<{ user: User }>("/auth/login", input);
      queryClient.setQueryData(ME, user);
    },
    [queryClient],
  );
  const logout = useCallback(async () => {
    await api.post("/auth/logout").catch(() => undefined);
    queryClient.clear();
    queryClient.setQueryData(ME, null);
  }, [queryClient]);

  const value = useMemo<AuthValue>(() => ({ user: me.data ?? null, loading: me.isPending, login, logout }), [me.data, me.isPending, login, logout]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth precisa do AuthProvider");
  return value;
}
